import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Send } from "lucide-react";

import { orpc } from "@/client";
import type { SendAuditRow } from "@/api/db/send-audit";
import { ToneChip } from "@/components/medico/tone-chip";
import { OperacaoLabels } from "@/lib/labels/operacao";
import { SystemLabels } from "@/lib/labels/system";

import { LogListSection, LogRow } from "./log-list";

export function AuditSection() {
	const [page, setPage] = useState(1);

	const { data, isLoading, isError } = useQuery(
		orpc.system.sendAudit.queryOptions({
			input: { page },
			placeholderData: keepPreviousData,
		}),
	);

	return (
		<LogListSection
			title={SystemLabels.sections.audit}
			dataComponent="system-audit"
			emptyIcon={Send}
			emptyTitle={SystemLabels.empty.audit}
			isLoading={isLoading}
			isError={isError}
			data={data}
			page={page}
			onPage={setPage}
			rowKey={(row) => row.id}
			renderRow={(row) => <AuditRow row={row} />}
		/>
	);
}

function AuditRow({ row }: { row: SendAuditRow }) {
	const detail = auditDetail(row);

	return (
		<LogRow
			data-slot="audit"
			data-outcome={row.outcome}
			person={row.person}
			chip={
				<ToneChip tone={SystemLabels.auditOutcomeTone[row.outcome]}>
					{SystemLabels.auditOutcome[row.outcome]}
				</ToneChip>
			}
			when={OperacaoLabels.moment(row.created_at)}
			{...detail}
		/>
	);
}

function auditDetail(row: SendAuditRow) {
	if (row.block_reason === null) {
		return {
			detail: row.preview,
			detailMeta: SystemLabels.auditAuthor(row.author),
			detailTone: "muted" as const,
		};
	}

	return {
		detail: SystemLabels.blockReason[row.block_reason],
		detailTone: "coral" as const,
	};
}
